import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  HelpBanner,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@betstarters/ui'
import { Ban, Plus, RotateCcw } from 'lucide-react'
import { api } from '../api'
import { useCapabilities } from '../hooks/useCapabilities'
import { useFinanceReference } from '../hooks/useReference'
import { messageOf } from '../lib/apiError'
import {
  CatalogToolbar,
  ErrorBanner,
  PageHeading,
  Pagination,
  StatusBadge,
} from '../components/catalog'
import { Combobox } from '../components/controls'
import {
  CausalForm,
  causalPayload,
  emptyCausalForm,
  type CausalFlags,
  type CausalFormValue,
} from '../components/financeForms'

export interface Causal {
  causalId: number
  causal: string
  causalFamilyId: number | null
  causalFamily: string
  causalGroupId: number
  causalGroup: string
  gameTypeId: number | null
  gameType: string
  transactionTypeId: number | null
  transactionType: string
  sign: number
  flags: CausalFlags
  isDisabledByPlatform: boolean
  creationDate: string
  lastModificationDate: string | null
}

interface CausalPage {
  causals: Causal[]
  total: number
  offset: number
  limit: number
}

const PAGE_SIZE = 50

/** SignBadge reads the direction of the movement at a glance. */
export function SignBadge({ sign }: { sign: number }) {
  const { t } = useTranslation('app')
  if (sign > 0) return <Badge variant="secondary">{t('causals.signs.in')}</Badge>
  if (sign < 0) return <Badge variant="outline">{t('causals.signs.out')}</Badge>
  return <Badge variant="outline">{t('causals.signs.neutral')}</Badge>
}

/**
 * CausalsPage is the wallet causal catalogue — [Catalog].[Wallet.Causal] in the
 * BsConnekto database. A causal is why money moved, and 25 flags on the row tell
 * the platform how to treat it; those are edited on the detail page, where they
 * have the room to be read.
 */
export function CausalsPage() {
  const { t } = useTranslation('app')
  const qc = useQueryClient()
  const { has } = useCapabilities()
  const canWrite = has('catalog:write')
  const { causalFamilies, causalGroups, gameTypes, transactionTypes } = useFinanceReference()

  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch] = useState('')
  const [familyFilter, setFamilyFilter] = useState('')
  const [groupFilter, setGroupFilter] = useState('')
  const [includeDisabled, setIncludeDisabled] = useState(true)
  const [offset, setOffset] = useState(0)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [form, setForm] = useState<CausalFormValue>(emptyCausalForm)
  const [error, setError] = useState('')

  const params = {
    search,
    includeDisabled: String(includeDisabled),
    offset,
    limit: PAGE_SIZE,
    ...(familyFilter ? { causalFamilyId: familyFilter } : {}),
    ...(groupFilter ? { causalGroupId: groupFilter } : {}),
  }

  const { data, isLoading } = useQuery({
    queryKey: ['causals', params],
    queryFn: async (): Promise<CausalPage> => {
      const res = await api.get('/causals', { params })
      return res.data as CausalPage
    },
  })

  const invalidate = () => qc.invalidateQueries({ queryKey: ['causals'] })

  const openCreate = () => {
    setForm(emptyCausalForm)
    setError('')
    setDialogOpen(true)
  }

  const save = useMutation({
    mutationFn: () =>
      api.post('/causals', { ...causalPayload(form, false), causalId: Number(form.causalId) }),
    onSuccess: () => {
      setDialogOpen(false)
      setError('')
      invalidate()
    },
    onError: (err) => setError(messageOf(err, t('causals.errors.save'))),
  })

  const toggle = useMutation({
    mutationFn: (c: Causal) =>
      c.isDisabledByPlatform
        ? api.post(`/causals/${c.causalId}/restore`)
        : api.delete(`/causals/${c.causalId}`),
    onSuccess: () => {
      setError('')
      invalidate()
    },
    onError: (err) => setError(messageOf(err, t('causals.errors.save'))),
  })

  const causals = data?.causals ?? []
  const total = data?.total ?? 0

  return (
    <div>
      <PageHeading
        title={t('causals.title')}
        subtitle={t('causals.subtitle')}
        action={
          canWrite && (
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger
                render={
                  <Button onClick={openCreate}>
                    <Plus />
                    {t('causals.newCausal')}
                  </Button>
                }
              />
              <DialogContent className="sm:max-w-2xl">
                <DialogHeader>
                  <DialogTitle>{t('causals.newCausal')}</DialogTitle>
                </DialogHeader>
                {/* The flags are not asked for here: a new causal starts with
                    all 25 off and gets its meaning on the detail page. */}
                <CausalForm
                  value={form}
                  onChange={setForm}
                  onSubmit={() => save.mutate()}
                  onCancel={() => setDialogOpen(false)}
                  families={causalFamilies}
                  groups={causalGroups}
                  gameTypes={gameTypes}
                  transactionTypes={transactionTypes}
                  isCreate
                  pending={save.isPending}
                  error={error}
                  submitLabel={t('common.create')}
                />
              </DialogContent>
            </Dialog>
          )
        }
      />

      <HelpBanner storageKey="causals">
        <p>{t('causals.help.line1')}</p>
        <p>{t('causals.help.line2')}</p>
      </HelpBanner>

      <CatalogToolbar
        searchInput={searchInput}
        onSearchInput={setSearchInput}
        onSubmitSearch={() => {
          setSearch(searchInput)
          setOffset(0)
        }}
        searchPlaceholder={t('causals.searchPlaceholder')}
        includeDisabled={includeDisabled}
        onToggleDisabled={() => {
          setIncludeDisabled(!includeDisabled)
          setOffset(0)
        }}
        filtered={search !== '' || !includeDisabled || familyFilter !== '' || groupFilter !== ''}
        onClear={() => {
          setSearch('')
          setSearchInput('')
          setFamilyFilter('')
          setGroupFilter('')
          setIncludeDisabled(true)
          setOffset(0)
        }}
      >
        <Combobox
          value={familyFilter}
          onValueChange={(v) => {
            setFamilyFilter(v)
            setOffset(0)
          }}
          items={causalFamilies.map((f) => ({ value: String(f.id), label: f.name }))}
          placeholder={t('causals.allFamilies')}
          className="w-48"
        />
        <Combobox
          value={groupFilter}
          onValueChange={(v) => {
            setGroupFilter(v)
            setOffset(0)
          }}
          items={causalGroups.map((g) => ({ value: String(g.id), label: g.name }))}
          placeholder={t('causals.allGroups')}
          className="w-48"
        />
      </CatalogToolbar>

      {!dialogOpen && <ErrorBanner message={error} />}

      {isLoading ? (
        <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
      ) : (
        <>
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/50">
                  <TableHead className="w-16">{t('causals.columns.id')}</TableHead>
                  <TableHead>{t('causals.columns.name')}</TableHead>
                  <TableHead className="w-40">{t('causals.columns.family')}</TableHead>
                  <TableHead className="w-40">{t('causals.columns.group')}</TableHead>
                  <TableHead className="w-24">{t('causals.columns.sign')}</TableHead>
                  <TableHead className="w-32">{t('common.columns.status')}</TableHead>
                  {canWrite && (
                    <TableHead className="w-20 text-right">{t('common.columns.actions')}</TableHead>
                  )}
                </TableRow>
              </TableHeader>
              <TableBody>
                {causals.map((c) => (
                  <TableRow key={c.causalId}>
                    <TableCell className="font-mono text-xs text-muted-foreground">
                      {c.causalId}
                    </TableCell>
                    <TableCell className="font-medium">
                      <Link to={`/causals/${c.causalId}`} className="hover:underline">
                        {c.causal}
                      </Link>
                    </TableCell>
                    <TableCell className="text-muted-foreground">{c.causalFamily || '—'}</TableCell>
                    <TableCell className="text-muted-foreground">{c.causalGroup || '—'}</TableCell>
                    <TableCell>
                      <SignBadge sign={c.sign} />
                    </TableCell>
                    <TableCell>
                      <StatusBadge disabled={c.isDisabledByPlatform} />
                    </TableCell>
                    {canWrite && (
                      <TableCell>
                        <div className="flex justify-end">
                          <Button
                            variant="ghost"
                            size="icon-sm"
                            title={c.isDisabledByPlatform ? t('common.restore') : t('common.retire')}
                            disabled={toggle.isPending}
                            onClick={() => toggle.mutate(c)}
                          >
                            {c.isDisabledByPlatform ? <RotateCcw /> : <Ban />}
                          </Button>
                        </div>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
                {causals.length === 0 && (
                  <TableRow>
                    <TableCell
                      colSpan={canWrite ? 7 : 6}
                      className="py-8 text-center text-muted-foreground"
                    >
                      {t('causals.empty')}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>

          <Pagination
            total={total}
            offset={offset}
            pageSize={PAGE_SIZE}
            onOffset={setOffset}
            label={t('causals.total', { count: total })}
          />
        </>
      )}
    </div>
  )
}
